// Pure rules for the monitoring timeline's staged edits; state and writes live in useStagedRouteEdits.
import type { Route, Stop } from "@/types/collections"

import { resequenceStops } from "./stagedStops"

interface StagedForRoute {
  routeId: string
}

/** Background sync keeps the local copy of any route with unsaved edits. */
export function keepUnsavedRoutes(
  fresh: Route[],
  previous: Route[],
  unsaved: ReadonlySet<string>
): Route[] {
  return fresh.map((route) => {
    if (!unsaved.has(route.id)) return route
    return previous.find((r) => r.id === route.id) ?? route
  })
}

export function describeStagedStops(
  adds: StagedForRoute[],
  removals: StagedForRoute[],
  routeId: string
): string {
  const added = adds.filter((add) => add.routeId === routeId).length
  const removed = removals.filter((removal) => removal.routeId === routeId).length
  const parts: string[] = []
  if (added > 0) parts.push(`${added} stop${added > 1 ? "s" : ""} added`)
  if (removed > 0) parts.push(`${removed} stop${removed > 1 ? "s" : ""} removed`)
  return parts.join(", ")
}

export function stagedStopCountDelta(
  adds: StagedForRoute[],
  removals: StagedForRoute[],
  routeId: string
): number {
  return (
    adds.filter((add) => add.routeId === routeId).length -
    removals.filter((removal) => removal.routeId === routeId).length
  )
}

export function revertStagedRoute(
  route: Route,
  originalStops: Stop[] | undefined,
  countDelta: number
): Route {
  return {
    ...route,
    stops: originalStops ?? route.stops,
    totalStops: route.totalStops - countDelta,
    isLocked: true,
  }
}

/** Skips removals whose stop is back on the route (e.g. after a date change refetched it). */
export function removalsToDelete<T extends StagedForRoute & { stopId: string }>(
  removals: T[],
  route: Route
): T[] {
  return removals
    .filter((removal) => removal.routeId === route.id)
    .filter((removal) => !route.stops.some((stop) => stop.id === removal.stopId))
}

export function withStagedStop(route: Route, stagedStop: Stop): Route {
  return {
    ...route,
    stops: resequenceStops([...route.stops, stagedStop]),
    totalStops: route.totalStops + 1,
    isLocked: false,
  }
}

export function withoutStop(route: Route, stopId: string): Route {
  return {
    ...route,
    stops: resequenceStops(route.stops.filter((s) => s.id !== stopId)),
    totalStops: Math.max(0, route.totalStops - 1),
  }
}
